import { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  TextInput,
  NumberInput,
  Textarea,
  Button,
  Stack,
  Group,
  Alert,
  Divider,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useSingleSourceForm,
  useUpdateSingleSourceForm,
  hasSingleSourceUploadedDocument,
} from '../hooks/useSingleSource';
import { useAuth } from '../../../auth/AuthContext';
import type { UpdateSingleSourceFormDto } from '../../../api/singleSource';
import { IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { RegulatoryClausePicker } from '../../../components/RegulatoryClausePicker';
import { syncThaanaFieldsFromDom, useThaanaFormSync } from '../../../hooks/useThaanaFormSync';
import { SingleSourceDocumentUpload } from '../components/SingleSourceDocumentUpload';
import { canEditSingleSourceForm } from '../permissions';

interface SingleSourceFormValues {
  formNumber: string;
  date: string;
  projectName: string;
  projectLocation: string;
  totalBudget?: number;
  amountAllocatedFromCurrentYearBudget?: number;
  totalExpenditureTillDate?: number;
  remainingBudget?: number;
  vendorName: string;
  proposedCost: number;
  proposedDuration: number;
  justification: string;
  detailsOfConditions?: string;
  decisionToSingleSource?: string;
}

export function SingleSourceEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: form, isLoading, error } = useSingleSourceForm(id!, { refetchOnMount: true });
  const updateForm = useUpdateSingleSourceForm();

  const formHook = useForm<SingleSourceFormValues>({
    initialValues: {
      formNumber: '',
      date: new Date().toISOString(),
      projectName: '',
      projectLocation: '',
      totalBudget: undefined,
      amountAllocatedFromCurrentYearBudget: undefined,
      totalExpenditureTillDate: undefined,
      remainingBudget: undefined,
      vendorName: '',
      proposedCost: 0,
      proposedDuration: 0,
      justification: '',
      detailsOfConditions: '',
      decisionToSingleSource: '',
    },
    validate: {
      formNumber: (value) => (!value ? 'Form number is required' : null),
      projectName: (value) => (!value ? 'Project name is required' : null),
      projectLocation: (value) => (!value ? 'Project location is required' : null),
      vendorName: (value) => (!value ? 'Vendor name is required' : null),
      proposedCost: (value) => (value <= 0 ? 'Cost must be greater than 0' : null),
      proposedDuration: (value) =>
        value <= 0 ? 'Duration must be greater than 0' : null,
      justification: (value) => (!value ? 'Justification is required' : null),
    },
  });

  useThaanaFormSync(formHook, {
    autoResizeSelector: '.auto-resize-textarea',
    enabled: !isLoading && !!form,
  });

  useEffect(() => {
    if (!form) return;

    formHook.setValues({
      formNumber: form.formNumber,
      date: form.date,
      projectName: form.projectName,
      projectLocation: form.projectLocation,
      totalBudget: form.totalBudget,
      amountAllocatedFromCurrentYearBudget: form.amountAllocatedFromCurrentYearBudget,
      totalExpenditureTillDate: form.totalExpenditureTillDate,
      remainingBudget: form.remainingBudget,
      vendorName: form.vendorName,
      proposedCost: form.proposedCost,
      proposedDuration: form.proposedDuration,
      justification: form.justification,
      detailsOfConditions: form.detailsOfConditions ?? '',
      decisionToSingleSource: form.decisionToSingleSource ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const buildUpdatePayload = (values: SingleSourceFormValues): UpdateSingleSourceFormDto => {
    const data: UpdateSingleSourceFormDto = {
      formNumber: values.formNumber,
      date: values.date || undefined,
      projectName: values.projectName,
      projectLocation: values.projectLocation,
      vendorName: values.vendorName,
      proposedCost: values.proposedCost,
      proposedDuration: values.proposedDuration,
      justification: values.justification,
    };

    if (values.totalBudget != null) data.totalBudget = values.totalBudget;
    if (values.amountAllocatedFromCurrentYearBudget != null) {
      data.amountAllocatedFromCurrentYearBudget = values.amountAllocatedFromCurrentYearBudget;
    }
    if (values.totalExpenditureTillDate != null) {
      data.totalExpenditureTillDate = values.totalExpenditureTillDate;
    }
    if (values.remainingBudget != null) data.remainingBudget = values.remainingBudget;
    if (values.detailsOfConditions?.trim()) {
      data.detailsOfConditions = values.detailsOfConditions;
    }
    if (values.decisionToSingleSource?.trim()) {
      data.decisionToSingleSource = values.decisionToSingleSource;
    }

    return data;
  };

  const handleSubmit = (values: SingleSourceFormValues) => {
    if (!form) return;

    if (form.status !== 'DRAFT' && form.status !== 'REJECTED') {
      notifications.show({
        title: 'Cannot save',
        message:
          'This form is no longer editable after submission. If it was rejected, refresh the page and try again.',
        color: 'orange',
      });
      return;
    }

    const data = buildUpdatePayload(values);

    setIsSubmitting(true);
    updateForm.mutate(
      { id: form.id, data },
      {
        onSuccess: () => {
          navigate(`/single-source/${form.id}`);
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Text>Loading...</Text>
      </Container>
    );
  }

  if (error || !form) {
    return (
      <Container size="md" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          variant="light"
        >
          Failed to load single source form. Please try again.
        </Alert>
      </Container>
    );
  }

  const currentFileName =
    form.doc && hasSingleSourceUploadedDocument(form.doc) ? form.doc.fileName : undefined;

  const canEdit = canEditSingleSourceForm(form, user);

  if (!canEdit) {
    return (
      <Container size="md" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Cannot edit"
          color="orange"
          variant="light"
        >
          This form can only be edited when it is in Draft or Rejected status, and only by a
          procurement officer, administrator or the person who created it.
        </Alert>
        <Button mt="md" variant="light" onClick={() => navigate(`/single-source/${form.id}`)}>
          Back to form
        </Button>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Edit Single Source Form: {form.formNumber}</Title>
          <Text c="dimmed" size="sm">
            {form.status === 'REJECTED'
              ? 'Update the form based on rejection feedback, then resubmit for review from the detail page.'
              : 'Update the form details below'}
          </Text>
        </div>

        <Card shadow="sm" p="lg">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              syncThaanaFieldsFromDom(formHook);
              formHook.onSubmit(handleSubmit)(e);
            }}
          >
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Form Number"
                  placeholder="e.g., SS/BC/HMH/2025/001"
                  required
                  {...formHook.getInputProps('formNumber')}
                />
                <DateTimePicker
                  label="Date"
                  placeholder="Select date"
                  required
                  value={formHook.values.date ? new Date(formHook.values.date) : null}
                  onChange={(value) =>
                    formHook.setFieldValue('date', value?.toISOString() || '')
                  }
                />
              </Group>

              <Divider label="Project Details" labelPosition="center" />

              <Group grow>
                <TextInput
                  label="Project Name (in Dhivehi)"
                  placeholder="Enter project name in Dhivehi"
                  required
                  className="thaana-input"
                  dir="rtl"
                  styles={{ input: { textAlign: 'right' } }}
                  name="projectName"
                  value={formHook.values.projectName}
                  onChange={(e) => {
                    formHook.setFieldValue('projectName', e.currentTarget.value);
                  }}
                  error={formHook.errors.projectName}
                />
                <TextInput
                  label="Project Location (in Dhivehi)"
                  placeholder="Enter project location in Dhivehi"
                  required
                  className="thaana-input"
                  dir="rtl"
                  styles={{ input: { textAlign: 'right' } }}
                  name="projectLocation"
                  value={formHook.values.projectLocation}
                  onChange={(e) => {
                    formHook.setFieldValue('projectLocation', e.currentTarget.value);
                  }}
                  error={formHook.errors.projectLocation}
                />
              </Group>

              <Divider label="Budget Information" labelPosition="center" />

              <Group grow>
                <NumberInput
                  label="Total Budget (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...formHook.getInputProps('totalBudget')}
                />
                <NumberInput
                  label="Amount Allocated from Current Year Budget (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...formHook.getInputProps('amountAllocatedFromCurrentYearBudget')}
                />
              </Group>

              <Group grow>
                <NumberInput
                  label="Total Expenditure Till Date (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...formHook.getInputProps('totalExpenditureTillDate')}
                />
                <NumberInput
                  label="Remaining Budget (MVR)"
                  placeholder="0.00"
                  min={0}
                  decimalScale={2}
                  {...formHook.getInputProps('remainingBudget')}
                />
              </Group>

              <Divider label="Vendor & Cost Information" labelPosition="center" />

              <Group grow>
                <TextInput
                  label="Vendor Name (in Dhivehi)"
                  placeholder="Enter vendor name in Dhivehi"
                  required
                  className="thaana-input"
                  dir="rtl"
                  styles={{ input: { textAlign: 'right' } }}
                  name="vendorName"
                  value={formHook.values.vendorName}
                  onChange={(e) => {
                    formHook.setFieldValue('vendorName', e.currentTarget.value);
                  }}
                  error={formHook.errors.vendorName}
                />
                <NumberInput
                  label="Proposed Cost (MVR)"
                  placeholder="0.00"
                  required
                  min={0}
                  decimalScale={2}
                  {...formHook.getInputProps('proposedCost')}
                />
              </Group>

              <NumberInput
                label="Proposed Duration (days)"
                placeholder="0"
                required
                min={1}
                {...formHook.getInputProps('proposedDuration')}
              />

              <Divider label="Justification & Conditions" labelPosition="center" />

              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    formHook.setFieldValue(
                      'justification',
                      `${formHook.values.justification || ''}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Justification (in Dhivehi)"
                placeholder="Explain why single source procurement is necessary in Dhivehi..."
                required
                minRows={4}
                className="thaana-input auto-resize-textarea paragraph-textarea"
                dir="rtl"
                styles={{
                  input: {
                    textAlign: 'right',
                    resize: 'none',
                    overflow: 'hidden',
                    minHeight: 'auto',
                  },
                }}
                name="justification"
                value={formHook.values.justification}
                onChange={(e) => {
                  formHook.setFieldValue('justification', e.currentTarget.value);
                }}
                error={formHook.errors.justification}
                onInput={(e) => {
                  const textarea = e.currentTarget;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />

              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    formHook.setFieldValue(
                      'detailsOfConditions',
                      `${formHook.values.detailsOfConditions || ''}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Details of Conditions Leading to Single Source Procurement (in Dhivehi)"
                placeholder="Provide detailed conditions in Dhivehi..."
                minRows={3}
                className="thaana-input auto-resize-textarea paragraph-textarea"
                dir="rtl"
                styles={{
                  input: {
                    textAlign: 'right',
                    resize: 'none',
                    overflow: 'hidden',
                    minHeight: 'auto',
                  },
                }}
                name="detailsOfConditions"
                value={formHook.values.detailsOfConditions || ''}
                onChange={(e) => {
                  formHook.setFieldValue('detailsOfConditions', e.currentTarget.value);
                }}
                error={formHook.errors.detailsOfConditions}
                onInput={(e) => {
                  const textarea = e.currentTarget;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />

              <Group justify="flex-end">
                <RegulatoryClausePicker
                  onInsert={(text) =>
                    formHook.setFieldValue(
                      'decisionToSingleSource',
                      `${formHook.values.decisionToSingleSource || ''}${text}`
                    )
                  }
                />
              </Group>
              <Textarea
                label="Decision to Single Source (in Dhivehi)"
                placeholder="Enter decision details in Dhivehi..."
                minRows={2}
                className="thaana-input auto-resize-textarea paragraph-textarea"
                dir="rtl"
                styles={{
                  input: {
                    textAlign: 'right',
                    resize: 'none',
                    overflow: 'hidden',
                    minHeight: 'auto',
                  },
                }}
                name="decisionToSingleSource"
                value={formHook.values.decisionToSingleSource || ''}
                onChange={(e) => {
                  formHook.setFieldValue('decisionToSingleSource', e.currentTarget.value);
                }}
                error={formHook.errors.decisionToSingleSource}
                onInput={(e) => {
                  const textarea = e.currentTarget;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${textarea.scrollHeight}px`;
                }}
              />

              <Divider label="Supporting Document" labelPosition="center" />

              {currentFileName && (
                <Text size="sm" c="dimmed">
                  Current document: {currentFileName}
                </Text>
              )}
              <SingleSourceDocumentUpload
                formId={form.id}
                currentFileName={currentFileName}
              />

              {updateForm.error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                  variant="light"
                >
                  {updateForm.error.response?.data?.error ||
                    'Failed to update single source form'}
                </Alert>
              )}

              <Group justify="flex-end" mt="xl">
                <Button
                  variant="light"
                  onClick={() => navigate(`/single-source/${form.id}`)}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                  Update Form
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
}
